import { html, LitElement, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { computePageRange } from './page-range.js';
import { styles } from './styles.js';

export class TtPaginator extends LitElement {
  static styles = styles;

  @property({ type: Number, attribute: 'total-rows' })
  totalRows = 0;

  @property({ type: Number, attribute: 'page-size' })
  pageSize = 25;

  @state()
  private currentPage = 1;

  private get totalPages() {
    return Math.ceil(this.totalRows / this.pageSize);
  }

  private get firstRow() {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  private get lastRow() {
    return Math.min(this.currentPage * this.pageSize, this.totalRows);
  }

  protected willUpdate(changed: Map<string, unknown>) {
    if (changed.has('totalRows') || changed.has('pageSize')) {
      this.currentPage = 1;
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    const keyMap: Record<string, number> = {
      ArrowRight: this.currentPage + 1,
      ArrowLeft: this.currentPage - 1,
      Home: 1,
      End: this.totalPages,
    };

    const targetPage = keyMap[e.key];
    if (targetPage === undefined) return;

    e.preventDefault();
    this.goToPage(targetPage);
  }

  render() {
    if (this.totalRows === 0) return nothing;

    const range = computePageRange(this.currentPage, this.totalPages);

    return html`
      <div class="paginator-row">
        <span class="small">Showing results ${this.firstRow} - ${this.lastRow} of ${this.totalRows}</span>
        ${this.totalPages > 1 ? html`
          <nav role="navigation" aria-label="Pagination" @keydown=${this.handleKeyDown}>
            <button class="small" data-theme="tertiary" aria-label="Go to first page" ?disabled=${this.currentPage === 1} @click=${() => this.goToPage(1)}>
              First
            </button>
            <button class="small" data-theme="tertiary" aria-label="Go to previous page" ?disabled=${this.currentPage === 1} @click=${() => this.goToPage(this.currentPage - 1)}>
              Previous
            </button>
            ${range.map(
              (page) => html`
                <button
                  class="small"
                  data-page=${page}
                  data-theme=${page === this.currentPage ? 'primary' : 'secondary'}
                  aria-label="Go to page ${page}"
                  aria-current=${page === this.currentPage ? 'page' : 'false'}
                  @click=${() => this.goToPage(page)}
                >
                  ${page}
                </button>
              `
            )}
            <button class="small" data-theme="tertiary" aria-label="Go to next page" ?disabled=${this.currentPage === this.totalPages} @click=${() => this.goToPage(this.currentPage + 1)}>
              Next
            </button>
            <button class="small" data-theme="tertiary" aria-label="Go to last page" ?disabled=${this.currentPage === this.totalPages} @click=${() => this.goToPage(this.totalPages)}>
              Last
            </button>
          </nav>
        ` : nothing}
      </div>
    `;
  }

  private goToPage(page: number) {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.dispatchEvent(
      new CustomEvent('page-change', {
        detail: { page: this.currentPage },
        bubbles: true,
        composed: true,
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tt-paginator': TtPaginator;
  }
}
